import { Badge, Box, HStack, Icon, Text, VStack } from '@elkaso-app/web-design';
import type { DealCategoryTreeNode } from 'apis/exclusive-deals';
import { FiChevronRight } from 'react-icons/fi';
import { isSelectableDepth } from './category-tree-helpers';

interface Props {
  /** Roots of the full tree. */
  tree: DealCategoryTreeNode[];
  /** Drill-down path; [0] is the root pick, [last] is the current pick. */
  selectedPath: DealCategoryTreeNode[];
  onSelect: (path: DealCategoryTreeNode[]) => void;
}

export const GridCategoryCascade = ({ tree, selectedPath, onSelect }: Props) => {
  // Column 0 = roots; each selected node with children contributes the next column.
  const columns: DealCategoryTreeNode[][] = [tree];
  for (const node of selectedPath) {
    if (node.children.length) columns.push(node.children);
  }

  const handleSelect = (columnIndex: number, node: DealCategoryTreeNode) => {
    onSelect([...selectedPath.slice(0, columnIndex), node]);
  };

  return (
    <Box height='420px' overflowX='auto' overflowY='hidden' borderWidth='1px' borderColor='gray.200' borderRadius='md'>
      <HStack spacing={0} align='stretch' height='100%' minW='fit-content'>
        {columns.map((items, columnIndex) => {
          const selectableColumn = isSelectableDepth(items[0]?.depth ?? columnIndex);
          return (
            <Box
              key={columnIndex === 0 ? 'root' : selectedPath[columnIndex - 1].id}
              width='240px'
              flexShrink={0}
              borderRight='1px solid'
              borderColor='gray.200'
              height='100%'
              display='flex'
              flexDirection='column'>
              <HStack
                justify='space-between'
                px='sm'
                py='xs'
                bg={selectableColumn ? 'red.50' : 'gray.50'}
                borderBottom='1px solid'
                borderColor='gray.200'>
                <Text fontSize='xs' fontWeight='semibold' color='gray.600' textTransform='uppercase'>
                  Depth {items[0]?.depth ?? columnIndex}
                </Text>
                {selectableColumn && (
                  <Badge colorScheme='red' variant='subtle' fontSize='10px'>
                    Selectable
                  </Badge>
                )}
              </HStack>
              <Box overflowY='auto' flex='1'>
                {items.length === 0 && (
                  <Text fontSize='sm' color='gray.500' p='md'>
                    No subcategories.
                  </Text>
                )}
                <VStack spacing={0} align='stretch'>
                  {items.map((node) => {
                    const isSelected = selectedPath[columnIndex]?.id === node.id;
                    return (
                      <HStack
                        key={node.id}
                        as='button'
                        type='button'
                        onClick={() => handleSelect(columnIndex, node)}
                        spacing={0}
                        justify='space-between'
                        px='sm'
                        py='xs'
                        bg={isSelected ? 'red.50' : 'white'}
                        color={isSelected ? 'red.700' : 'gray.800'}
                        _hover={{ bg: isSelected ? 'red.50' : 'gray.50' }}
                        cursor='pointer'>
                        <Text
                          fontSize='sm'
                          textAlign='left'
                          fontWeight={isSelected ? 'semibold' : 'normal'}
                          noOfLines={2}>
                          {node.title || `Untitled (${node.id})`}
                        </Text>
                        <Icon
                          as={FiChevronRight}
                          boxSize='14px'
                          color={isSelectableDepth(node.depth) ? 'red.400' : 'gray.400'}
                        />
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>
            </Box>
          );
        })}
      </HStack>
    </Box>
  );
};
